/* eslint-disable quote-props */
import { Theme, createTheme } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface PaletteColor {
    lighter?: string;
    darker?: string;
  }

  interface SimplePaletteColorOptions {
    lighter?: string;
    darker?: string;
  }

  // eslint-disable-next-line no-unused-vars
  interface Palette {
    white: PaletteColor;
    whiteWarm: PaletteColor;
    taupe: PaletteColor;
    granite: PaletteColor;
    mint: PaletteColor;
    sand: PaletteColor;
    gray: PaletteColor & { darker: string };
    purple: PaletteColor;
    torquise: PaletteColor;
    black: PaletteColor & { darker: string };
    neutral: PaletteColor & { lighter: string };
    green: PaletteColor & { lighter: string };
    yellow: PaletteColor & { lighter: string };
    red: PaletteColor & { lighter: string };
    violet: PaletteColor & { lighter: string };
  }
  // eslint-disable-next-line no-unused-vars
  interface PaletteOptions {
    white?: SimplePaletteColorOptions;
    whiteWarm?: SimplePaletteColorOptions;
    taupe?: SimplePaletteColorOptions;
    granite?: SimplePaletteColorOptions;
    mint?: SimplePaletteColorOptions;
    sand?: SimplePaletteColorOptions;
    gray?: SimplePaletteColorOptions & { darker?: string };
    purple?: SimplePaletteColorOptions;
    torquise?: SimplePaletteColorOptions;
    black?: SimplePaletteColorOptions & { darker?: string };
    neutral?: SimplePaletteColorOptions & { lighter?: string };
    green?: SimplePaletteColorOptions & { lighter?: string };
    yellow?: SimplePaletteColorOptions & { lighter?: string };
    red?: SimplePaletteColorOptions & { lighter?: string };
    violet?: SimplePaletteColorOptions & { lighter?: string };
  }
}

export const paletteColors = {
  primary: {
    main: "#FEC20B",
    light: "#F9EED5",
    dark: "#F7DC8D",
  },
  secondary: {
    main: "#FAF6F4",
  },
  info: {
    main: "#7588DF",
    light: "#E6E5F1",
  },
  error: {
    main: "#ED7857",
    light: "#F7E3DC",
    iconColor: "#262626",
  },
  warning: {
    main: "#262626",
    light: "#F9EED5",
    dark: "#AE8305",
  },
  success: {
    main: "#459796",
    light: "#DBE7E7",
  },
  white: {
    main: "#ffffff",
  },
  whiteWarm: {
    main: "#FAF6F4",
  },
  taupe: {
    dark: "#E8DFD7",
    main: "#D9C7B8",
    light: "#D6C7BA",
  },
  granite: {
    main: "#6F6661",
  },
  mint: {
    main: "#9AD9B7",
  },
  sand: {
    main: "#F4EFEB",
  },
  gray: {
    main: "#696969",
    light: "#C8C8C8",
    dark: "#908E8D",
    darker: "#6B6B6B",
  },
  purple: {
    main: "#B8BFE9",
  },
  torquise: {
    main: "#95C6C5",
    light: "#DBE7E7",
  },
  black: {
    main: "#262626",
    dark: "#1A1A1A",
    darker: "#0A0A0A",
  },
  neutral: {
    lighter: "#F4F4F4",
    light: "#E8E4DD",
    main: "#DAD7D6",
    dark: "#99A1AF",
  },
  green: {
    light: "#D4F4DD",
    main: "#0F5132",
    lighter: "#D0E7D7",
    dark: "#537561",
  },
  yellow: {
    light: "#FFF3CD",
    main: "#856404",
    lighter: "#E8B949",
  },
  red: {
    light: "#FFCDCD",
    main: "#721C24",
    lighter: "#F7E3DC",
    dark: "#D35633",
  },
  violet: {
    light: "#ECE5F1",
    main: "#7754BD",
    lighter: "#E6E5F1",
    dark: "#637AE2",
  },
};

export const defaultTheme: Theme = createTheme({
  typography: {
    fontFamily: ["geograph-regular", "-apple-system", "BlinkMacSystemFont", "Arial", "sans-serif"].join(","),
  },
  palette: {
    ...paletteColors,
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        variant: "contained",
      },
      styleOverrides: {
        root: {
          borderRadius: "48px",
          height: "48px",
          padding: "0 1.25rem",
          fontFamily: "geograph-regular",
          letterSpacing: "0.34px",
          textTransform: "none",
        },
      },
      variants: [
        {
          props: {
            size: "small",
          },
          style: {
            height: "32px",
            fontSize: "10px",
            fontWeight: "bold",
            minWidth: "auto",
            fontFamily: "geograph-regular",
            letterSpacing: "0.24px",
          },
        },
        {
          props: {
            color: "error",
          },
          style: {
            color: "white",
          },
        },
        {
          props: {
            variant: "outlined",
            color: "primary",
          },
          style: {
            border: `1px solid ${paletteColors.black.main}`,
            color: "#000",
            ":hover": {
              border: `1px solid ${paletteColors.black.main}`,
              background: paletteColors.taupe.dark,
            },
            ":disabled": {
              background: "none",
            },
          },
        },
        {
          props: {
            variant: "contained",
            color: "primary",
          },
          style: {
            border: "1px solid transparent",
            ":hover": {
              background: paletteColors.black.main,
              color: paletteColors.white.main,
              border: `1px solid ${paletteColors.whiteWarm.main}`,
            },
            ":disabled": {
              background: paletteColors.primary.light,
            },
          },
        },
        {
          props: {
            variant: "contained",
            color: "secondary",
          },
          style: {
            background: "white",
          },
        },
        {
          props: {
            variant: "text",
          },
          style: {
            border: "1px solid transparent",
            color: paletteColors.black.main,
            letterSpacing: "0.024em",
            background: "transparent",
            ":hover": {
              color: paletteColors.primary.main,
              background: "transparent",
            },
            ":disabled": {
              color: paletteColors.gray.dark,
            },
          },
        },
        {
          props: {
            variant: "text",
            color: "primary",
          },
          style: {},
        },
        {
          props: {
            variant: "text",
            color: "secondary",
          },
          style: {
            fontFamily: "atlas-grotesk-regular",
          },
        },
      ],
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: {
          transform: "translate(0, -1.5px) scale(1)",
          color: "black",
          fontSize: "13px",
          "&.Mui-focused": {
            color: "rgba(0, 0, 0, 0.6)",
          },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: "standard",
      },
      styleOverrides: {
        root: {
          marginBottom: "1rem",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: "2px",
          marginBottom: "1rem",
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "none",
          fontWeight: 600,
          fontSize: "14px",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: paletteColors.sand.main,
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
  zIndex: {
    drawer: 1200,
    modal: 1500,
  },
});

/**
 * Typography
 */
defaultTheme.typography.h1 = {
  fontSize: "32px",
  fontWeight: 300,
  fontFamily: "grenette-light-pro",
  letterSpacing: "-0.02em",
  lineHeight: "40px",
  cursor: "default",
};

defaultTheme.typography.h2 = {
  fontSize: "20px",
  fontWeight: 400,
  lineHeight: "28px",
  cursor: "default",
};

defaultTheme.typography.h3 = {
  fontSize: "18px",
  fontWeight: 400,
  letterSpacing: "-0.02em",
  lineHeight: "24px",
  cursor: "default",
};

defaultTheme.typography.h4 = {
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: "24px",
  letterSpacing: "-0.02em",
  cursor: "default",
};

defaultTheme.typography.body1 = {
  fontSize: "14px",
  lineHeight: "20px",
  cursor: "default",
};

defaultTheme.typography.subtitle1 = {
  fontSize: "0.75rem",
  fontWeight: 400,
  lineHeight: "19px",
  cursor: "default",
};

defaultTheme.typography.subtitle2 = {
  fontSize: "1.05956rem",
  fontWeight: 700,
  lineHeight: "20px",
  cursor: "default",
};
